import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { TranslationService } from '../services/translation.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SILENT_HTTP } from './silent-http.context';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const translationService = inject(TranslationService);
  const token = authService.isTokenValid() ? authService.token : null;
  let authReq = req;

  if (token) {
    authReq = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  const router = inject(Router);
  const snackBar = inject(MatSnackBar);
  const silent = req.context.get(SILENT_HTTP);

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // status 0: aborted navigation, CORS, or offline — never force logout.
      // Only toast when the browser is actually offline (not for cancelled XHR).
      if (error.status === 0) {
        if (!silent && typeof navigator !== 'undefined' && !navigator.onLine) {
          const offlineMsg = translationService.translate('common.loadFailed') || 'Network offline. Check your connection.';
          const closeBtn = translationService.translate('common.close') || 'Close';
          snackBar.open(offlineMsg, closeBtn, {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            panelClass: ['error-snackbar'],
          });
        }
        return throwError(() => error);
      }

      const deactivated =
        error.status === 403 && error.error?.error?.code === 'ACCOUNT_DEACTIVATED';

      if (error.status === 401 || deactivated) {
        // Background polls should not force logout on a single flaky 401
        // (e.g. race during token refresh / tab wake). User-facing calls still do.
        if (!silent) {
          authService.logout();
          router.navigate(['/login']);
        }
      } else if (!silent) {
        const url = req.url;
        const isPublicAuthFlow =
          url.includes('/public/') ||
          url.includes('/auth/login') ||
          (error.status === 403 && url.includes('/auth/'));
        const isDialogHandledOrderAction =
          url.includes('/verify-payment') ||
          url.includes('/reject-payment');
        if (isPublicAuthFlow || isDialogHandledOrderAction) {
          return throwError(() => error);
        }
        const apiError = error.error?.error;
        const validationDetail = apiError?.details?.[0]?.msg;
        const rawErrMsg =
          apiError?.message ||
          validationDetail ||
          error.error?.message ||
          error.statusText ||
          'An unknown error occurred';
        const translatedErrMsg = translationService.translate(rawErrMsg);
        const errorPrefix = translationService.translate('common.error') || 'Error';
        const closeBtn = translationService.translate('common.close') || 'Close';
        snackBar.open(`${errorPrefix}: ${translatedErrMsg}`, closeBtn, {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar']
        });
      }
      return throwError(() => error);
    })
  );
};
